<template>
  <div class="data-container">
    <!-- ▸ BREADCRUMB -->
    <header id="data-chart" class="breadcrumb">
      <div class="breadcrumb-left">
        <span class="device-name">Reports</span>
        <span class="meta-divider">•</span>

        <div class="header-meta-group">
          <span class="meta-label">Totale</span>
          <span class="meta-value">{{ totalCount }}</span>

          <span class="meta-label" style="margin-left: 8px">Non lette</span>
          <span class="meta-value" :class="{ 'unread-badge': unreadCount > 0 }">
            {{ unreadCount }}
          </span>
        </div>
      </div>

      <div class="breadcrumb-actions">
        <button
          class="btn btn-export"
          @click="exportToPDF"
          :disabled="notifications.length === 0 || exporting"
        >
          <span v-if="exporting" class="export-spinner"></span>
          <span v-else>📄 Esporta PDF</span>
        </button>

        <button
          class="btn btn-mark-all"
          @click="markAllAsRead"
          :disabled="unreadCount === 0"
        >
          Segna tutte come lette
        </button>
      </div>
    </header>

    <!-- ▸ FILTRI -->
    <div class="filters-section">
      <div class="filters-row">
        <!-- Filtro Periodo -->
        <div class="filter-group">
          <label class="filter-label">Periodo:</label>
          <div class="range-btn-group">
            <button
              class="range-btn"
              :class="{ active: timeRange === 'day' }"
              @click="setTimeRange('day')"
            >
              Oggi
            </button>
            <button
              class="range-btn"
              :class="{ active: timeRange === 'week' }"
              @click="setTimeRange('week')"
            >
              Settimana
            </button>
            <button
              class="range-btn"
              :class="{ active: timeRange === 'month' }"
              @click="setTimeRange('month')"
            >
              Mese
            </button>
            <button
              class="range-btn"
              :class="{ active: timeRange === 'all' }"
              @click="setTimeRange('all')"
            >
              Tutto
            </button>
          </div>
        </div>

        <!-- Datepicker personalizzato -->
        <div class="filter-group">
          <label class="filter-label">Da:</label>
          <input
            type="date"
            v-model="startDate"
            :max="endDate"
            class="date-input"
            @change="applyDateFilter"
          />

          <label class="filter-label">A:</label>
          <input
            type="date"
            v-model="endDate"
            :min="startDate"
            :max="today"
            class="date-input"
            @change="applyDateFilter"
          />

          <button class="btn btn-small btn-reset-date" @click="resetDateFilter">
            Reset
          </button>
        </div>

        <!-- Filtri aggiuntivi -->
        <div class="filter-group">
          <label class="filter-label">Filtra:</label>
          <div class="range-btn-group">
            <button
              class="range-btn"
              :class="{ active: scope === 'all' }"
              @click="scope = 'all'"
            >
              Tutte
            </button>
            <button
              class="range-btn"
              :class="{ active: scope === 'unread' }"
              @click="scope = 'unread'"
            >
              Non lette
            </button>
          </div>
        </div>

        <div class="filter-group">
          <label class="filter-label">Mostra lette:</label>
          <div class="toggle-switch">
            <input
              type="checkbox"
              :id="'toggle-read'"
              v-model="includeRead"
              @change="loadNotifications"
            />
            <label :for="'toggle-read'" class="toggle-slider"></label>
          </div>
        </div>

        <div class="filter-group">
          <label class="filter-label">Tipo di report:</label>
          <select
            class="custom-select"
            v-model="selectedSeverity"
            @change="loadNotifications"
          >
            <option value="all">Tutti</option>
            <option value="success">Successo</option>
            <option value="info">Informazione</option>
            <option value="warning">Avviso</option>
            <option value="error">Errore</option>
          </select>
        </div>
      </div>

      <!-- Riepilogo filtro attivo -->
      <div v-if="activeFilterSummary" class="filter-summary">
        <span class="summary-text">{{ activeFilterSummary }}</span>
        <button class="btn-clear-filter" @click="clearAllFilters">
          ✕ Rimuovi filtri
        </button>
      </div>
    </div>

    <!-- ▸ STATISTICHE RAPIDE -->
    <div class="stats-section">
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-content">
            <div class="stat-value">{{ notifications.length }}</div>
            <div class="stat-label">Reports totali</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-content">
            <div class="stat-value" :class="{ 'unread-stat': unreadCount > 0 }">
              {{ unreadCount }}
            </div>
            <div class="stat-label">Non lette</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-content">
            <div class="stat-value">{{ errorCount }}</div>
            <div class="stat-label">Errori</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-content">
            <div class="stat-value">
              {{ oldestDate ? formatDateShort(oldestDate) : "N/A" }}
            </div>
            <div class="stat-label">Dal</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ▸ CONTENUTO -->
    <div class="content-wrapper">
      <div class="notifications-container">
        <!-- LOADING -->
        <div v-if="loading" class="loading-placeholder">
          <div class="loading-spinner"></div>
          <span>Caricamento reports...</span>
        </div>

        <!-- LISTA VUOTA -->
        <div v-else-if="notifications.length === 0" class="empty-state">
          <div class="empty-icon">📭</div>
          <h3>Nessuna notifica</h3>
          <p>{{ getEmptyStateMessage() }}</p>
          <button
            v-if="hasActiveFilters"
            class="btn btn-small"
            @click="clearAllFilters"
          >
            Rimuovi filtri
          </button>
        </div>

        <!-- LISTA NOTIFICHE -->
        <div v-else class="notifications-list">
          <div
            v-for="notification in notifications"
            :key="notification.id"
            class="notification-card"
            :class="{
              unread: !notification.is_read,
              read: notification.is_read,
              [`severity-${notification.severity}`]: true,
            }"
          >
            <div class="notification-header">
              <div
                class="notification-badge"
                :class="notification.severity"
              ></div>
              <div class="notification-title">
                <h4>{{ notification.title }}</h4>
                <div class="notification-meta">
                  <span class="meta-date">{{
                    formatDate(notification.createdAt)
                  }}</span>
                  <span v-if="notification.graph_name" class="meta-graph">
                    • {{ notification.graph_name }}
                  </span>
                  <span v-if="notification.username" class="meta-user">
                    • {{ notification.username }}
                  </span>
                </div>
              </div>
              <div class="notification-actions">
                <button
                  class="action-btn"
                  @click.stop="toggleRead(notification)"
                  :title="
                    notification.is_read
                      ? 'Segna come non letta'
                      : 'Segna come letta'
                  "
                >
                  {{ notification.is_read ? "📭" : "📬" }}
                </button>
                <button
                  class="action-btn delete-btn"
                  @click.stop="deleteNotification(notification.id)"
                  title="Elimina"
                >
                  🗑️
                </button>
              </div>
            </div>

            <div class="notification-body">
              <p class="notification-message">{{ notification.message }}</p>

              <div
                v-if="
                  notification.admin_message &&
                  notification.admin_message !== notification.message
                "
                class="admin-message"
              >
                <div class="admin-label">Messaggio amministrativo:</div>
                <p>{{ notification.admin_message }}</p>
              </div>

              <div v-if="notification.payload" class="notification-payload">
                <details>
                  <summary>Dettagli</summary>
                  <pre>{{ formatPayload(notification.payload) }}</pre>
                </details>
              </div>
            </div>

            <div class="notification-footer">
              <div class="notification-type">
                <span class="type-badge">{{ notification.type }}</span>
                <span class="severity-badge" :class="notification.severity">
                  {{ getSeverityLabel(notification.severity) }}
                </span>
              </div>
              <div
                v-if="notification.graph_id"
                class="notification-actions-footer"
              >
                <button
                  v-if="notification.graph_id"
                  class="btn btn-small"
                  @click.stop="goToDevice(notification.graph_id)"
                >
                  Vai al dispositivo
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- PAGINAZIONE -->
        <div
          v-if="notifications.length > 0 && totalCount > limit"
          class="pagination"
        >
          <button
            class="pagination-btn"
            :disabled="offset === 0"
            @click="previousPage"
          >
            ← Precedenti
          </button>

          <div class="pagination-info">
            {{ offset + 1 }} - {{ Math.min(offset + limit, totalCount) }} di
            {{ totalCount }}
          </div>

          <button
            class="pagination-btn"
            :disabled="offset + limit >= totalCount"
            @click="nextPage"
          >
            Successivi →
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toast-notification";
import axios from "@/axiosInstance";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

/* ───────── TYPES ───────── */
interface Notification {
  id: number;
  user_id: number;
  username: string | null;
  email: string | null;
  graph_id: number | null;
  graph_name: string | null;
  graph_status: string | null;
  type: string;
  title: string;
  message: string;
  admin_message?: string;
  severity: string;
  payload: any;
  createdAt: string;
  is_read: boolean;
}

/* ───────── STATE ───────── */
const router = useRouter();
const toast = useToast();

const notifications = ref<Notification[]>([]);
const loading = ref(false);
const exporting = ref(false);
const totalCount = ref(0);
const unreadCount = ref(0);

// Filtri
const scope = ref<"all" | "unread">("unread");
const includeRead = ref(true);
const selectedSeverity = ref("all");
const timeRange = ref<"day" | "week" | "month" | "all" | "custom">("week");
const startDate = ref("");
const endDate = ref("");

// Paginazione
const limit = ref(20);
const offset = ref(0);

// Date utilities
const today = ref(new Date().toISOString().split("T")[0]);

/* ───────── COMPUTED ───────── */
const queryParams = computed(() => {
  // console.log("selectedSeverity:", selectedSeverity.value);
  const params: any = {
    scope: scope.value,
    include_read: includeRead.value ? "1" : "0",
    limit: limit.value,
    offset: offset.value,
    severity:
      selectedSeverity.value !== "all" ? selectedSeverity.value : undefined,
  };

  // Gestione date in ordine di priorità:
    // 1. Se c'è un range predefinito (non "all" e non "custom"), usa quello
    // 2. Se ci sono date custom, usa quelle
    // 3. Se timeRange è "all", non inviare date
  
  if (timeRange.value !== "all") {
    if (timeRange.value !== "custom") {
      // Range predefinito (day, week, month)
      const range = calculateDateRange(timeRange.value);
      params.start_date = range.start;
      params.end_date = range.end;
    } else if (startDate.value && endDate.value) {
      // Date custom
      params.start_date = startDate.value;
      params.end_date = endDate.value;
    }
    // Se timeRange è "custom" ma non ci sono date, non inviare nulla
  }
  
  return params;
});

const activeFilterSummary = computed(() => {
  if (timeRange.value === "custom" && startDate.value && endDate.value) {
    return `Periodo: ${formatDateDisplay(
      startDate.value
    )} - ${formatDateDisplay(endDate.value)}`;
  }

  switch (timeRange.value) {
    case "day":
      return "Oggi";
    case "week":
      return "Ultima settimana";
    case "month":
      return "Ultimo mese";
    case "all":
      return "Tutto il periodo";
    default:
      return null;
  }
});

const hasActiveFilters = computed(() => {
  return (
    scope.value !== "all" ||
    !includeRead.value ||
    selectedSeverity.value !== "all" ||
    timeRange.value !== "week" ||
    startDate.value !== "" ||
    endDate.value !== ""
  );
});

const errorCount = computed(() => {
  return notifications.value.filter((n) => n.severity === "error").length;
});

const oldestDate = computed(() => {
  if (notifications.value.length === 0) return null;
  return notifications.value.reduce((oldest, notification) => {
    const date = new Date(notification.createdAt);
    return date < oldest ? date : oldest;
  }, new Date(notifications.value[0].createdAt));
});

/* ───────── METHODS ───────── */
function calculateDateRange(range: "day" | "week" | "month") {
  const end = new Date();
  const start = new Date();

  switch (range) {
    case "day":
      start.setDate(end.getDate() - 1);
      break;
    case "week":
      start.setDate(end.getDate() - 7);
      break;
    case "month":
      start.setMonth(end.getMonth() - 1);
      break;
  }

  return {
    start: start.toISOString().split("T")[0],
    end: end.toISOString().split("T")[0],
  };
}

function setTimeRange(range: "day" | "week" | "month" | "all" | "custom") {
  timeRange.value = range;

  if (range === "all") {
    startDate.value = "";
    endDate.value = "";
  } else if (range !== "custom") {
    const dates = calculateDateRange(range);
    startDate.value = dates.start;
    endDate.value = dates.end;
  }

  loadNotifications();
}

function applyDateFilter() {
  if (startDate.value && endDate.value) {
    // Quando l'utente modifica manualmente le date, 
    // imposta timeRange a "custom"
    timeRange.value = "custom";
    loadNotifications();
  } else if (!startDate.value && !endDate.value) {
    // Se l'utente cancella entrambe le date, 
    // torna al range predefinito "week"
    timeRange.value = "day";
    const dates = calculateDateRange("day");
    startDate.value = dates.start;
    endDate.value = dates.end;
    loadNotifications();
  }
}

function resetDateFilter() {
  startDate.value = "";
  endDate.value = "";
  timeRange.value = "day";

  // Carica le date del range predefinito
  const dates = calculateDateRange("day");
  startDate.value = dates.start;
  endDate.value = dates.end;

  loadNotifications();
}

function clearAllFilters() {
  scope.value = "all";
  includeRead.value = true;
  selectedSeverity.value = "all";
  timeRange.value = "day";
   // Reset date con il range predefinito
  const dates = calculateDateRange("day");
  startDate.value = dates.start;
  endDate.value = dates.end;
  loadNotifications();
}

async function loadNotifications() {
  try {
    loading.value = true;

    const params = new URLSearchParams();
    // console.log("queryparams.value", queryParams.value);
    Object.entries(queryParams.value).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, String(value));
      }
    });
    console.log("Caricamento notifiche con params:", params);
    const response = await axios.get(`/notifications?${params.toString()}`);

    notifications.value = response.data.items;
    console.log("notifiche caricate:", notifications.value);
    totalCount.value = response.data.totalCount || notifications.value.length;
    unreadCount.value = response.data.unreadCount || 0;
  } catch (error) {
    console.error("Errore nel caricamento notifiche:", error);
    toast.error("Errore nel caricamento delle notifiche", {
      position: "top-right",
    });
  } finally {
    loading.value = false;
  }
}

async function toggleRead(notification: Notification) {
  try {
    const newReadStatus = !notification.is_read;
    // console.log("newReadStatus:", newReadStatus);

    const resReadUnread = await axios.put(
      `/notifications/${notification.id}/read`,
      {
        is_read: newReadStatus,
      }
    );
    // console.log("filtrolettenonlette", resReadUnread);
    // Aggiorna localmente
    notification.is_read = newReadStatus;

    // Aggiorna contatori
    if (newReadStatus) {
      unreadCount.value = Math.max(0, unreadCount.value - 1);
    } else {
      unreadCount.value += 1;
    }

    // Se siamo in modalità "unread" e segniamo come letta, rimuovi dalla lista
    if (scope.value === "unread") {
      notifications.value = notifications.value.filter(
        (n) => n.id !== notification.id
      );
    }
    if (resReadUnread.status === 200) {
      toast.success(
        `Notifica ${
          newReadStatus ? "segna come letta" : "segna come non letta"
        }`,
        {
          position: "top-right",
          duration: 2000,
        }
      );
    }
  } catch (error) {
    console.error("Errore nell'aggiornamento notifica:", error);
    toast.error("Errore nell'aggiornamento della notifica", {
      position: "top-right",
    });
  }
}

async function markAllAsRead() {
  try {
    await axios.post("/notifications/read-all");

    // Aggiorna tutte le notifiche localmente
    notifications.value.forEach((notification) => {
      notification.is_read = true;
    });

    // Resetta contatore non lette
    unreadCount.value = 0;

    toast.success("Tutte le notifiche segnate come lette", {
      position: "top-right",
      duration: 2000,
    });
  } catch (error) {
    console.error("Errore nel segnare tutte come lette:", error);
    toast.error("Errore nel segnare tutte come lette", {
      position: "top-right",
    });
  }
}

async function deleteNotification(id: number) {
  if (!confirm("Sei sicuro di voler eliminare questa notifica?")) return;

  try {
    await axios.delete(`/notifications/${id}`);

    // Rimuovi localmente
    notifications.value = notifications.value.filter((n) => n.id !== id);

    toast.success("Notifica eliminata", {
      position: "top-right",
      duration: 2000,
    });
  } catch (error) {
    console.error("Errore nell'eliminazione notifica:", error);
    toast.error("Errore nell'eliminazione della notifica", {
      position: "top-right",
    });
  }
}

function goToDevice(graphId: number) {
  router.push(`/data/${graphId}`);
}

function formatDate(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDateShort(date: Date) {
  return date.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateDisplay(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatPayload(payload: any) {
  // console.log("formatPayload called with:", payload);
  
  if (!payload) return "Nessun dettaglio disponibile";
  
  let parsedPayload;
  
  // Parse JSON string if needed
  if (typeof payload === "string") {
    try {
      parsedPayload = JSON.parse(payload);
    } catch {
      return payload;
    }
  } else {
    parsedPayload = payload;
  }
  
  // Custom formatting for specific payload structures
  if (parsedPayload && typeof parsedPayload === "object") {
    return formatObjectForDisplay(parsedPayload);
  }
  
  return JSON.stringify({...parsedPayload, }, null, 2);
}

function formatObjectForDisplay(obj: any): string {
  const sections: string[] = [];
  
  // Device section
  if (obj.graphId !== undefined) {
    sections.push(`DISPOSITIVO`);
    sections.push(`   Numero: ${obj.graphId}`);
  }

  if (obj.graph_name !== undefined) {
    sections.push(`   Nome: ${obj.graph_name}`);
  }
  
  // Status section
  if (obj.is_static !== undefined) {
    const status = obj.is_static ? "Statico" : "In movimento";
    sections.push(`   Stato: ${status}`);
  }
  
  // Add separator if we have both device info and user info
  if ((obj.graphId !== undefined || obj.is_static !== undefined) && 
      (obj.byUserId !== undefined || obj.byUserUsername !== undefined || obj.byUserEmail !== undefined)) {
    sections.push(`\n──────────────\n`);
  }
  
  // User credentials section
  if (obj.byUserId !== undefined || obj.byUserUsername !== undefined || obj.byUserEmail !== undefined) {
    sections.push(`CREDENZIALI`);
    
    if (obj.byUserId !== undefined) {
      sections.push(`   Numero ID: ${obj.byUserId}`);
    }
    
    if (obj.byUserUsername !== undefined) {
      sections.push(`   Username: ${obj.byUserUsername}`);
    }
    
    if (obj.byUserEmail !== undefined) {
      sections.push(`   Email: ${obj.byUserEmail}`);
    }
  }
  
  // Handle other fields that might be present
  const knownFields = ['graphId', 'is_static', 'byUserId', 'byUserUsername', 'byUserEmail', 
                      'userId', 'username', 'email', 'graph_id', 'user_id', 'graph_name'];
  
  const otherFields = Object.keys(obj).filter(key => !knownFields.includes(key));
  
  if (otherFields.length > 0) {
    if (sections.length > 0 && !sections[sections.length - 1].includes('──────────────')) {
      sections.push(`\n──────────────\n`);
    }
    
    sections.push(`🔧 ALTRI DETTAGLI`);
    
    otherFields.forEach(key => {
      const value = obj[key];
      const formattedKey = formatKey(key);
      sections.push(`   ${formattedKey}: ${formatValue(value)}`);
    });
  }
  
  return sections.join('\n');
}

function formatKey(key: string): string {
  const keyMap: Record<string, string> = {
    'graphId': 'Device ID',
    'graph_id': 'Device ID',
    'is_static': 'Modalità',
    'byUserId': 'Utente ID',
    'user_id': 'Utente ID',
    'userId': 'Utente ID',
    'byUserUsername': 'Username',
    'username': 'Username',
    'byUserEmail': 'Email',
    'email': 'Email',
    'temperature': 'Temperatura',
    'humidity': 'Umidità',
    'voltage': 'Voltaggio',
    'min': 'Minimo',
    'max': 'Massimo',
    'entity_id': 'Entità ID',
    'kind': 'Tipo',
    'metric': 'Metrica',
    'subtype': 'Sottotipo',
    'measurement_ts': 'Data misurazione',
    'created_at': 'Creato il',
    'updated_at': 'Aggiornato il'
  };
  
  return keyMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function formatValue(value: any): string {
  if (value === null || value === undefined) return 'N/A';
  
  if (typeof value === 'boolean') {
    return value ? 'Sì' : 'No';
  }
  
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  
  // Check if it's a date string
  if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleString('it-IT', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      }
    } catch (e) {
      // Not a valid date, continue
    }
  }
  
  return String(value);
}


function getSeverityLabel(severity: string) {
  const labels: Record<string, string> = {
    success: "Successo",
    info: "Informazione",
    warning: "Avviso",
    error: "Errore",
  };
  return labels[severity] || severity;
}

function getEmptyStateMessage() {
  if (hasActiveFilters.value) {
    return "Nessuna notifica corrisponde ai filtri selezionati.";
  }
  return scope.value === "unread"
    ? "Non ci sono notifiche non lette"
    : "Non ci sono notifiche da visualizzare";
}

function previousPage() {
  offset.value = Math.max(0, offset.value - limit.value);
  loadNotifications();
}

function nextPage() {
  if (offset.value + limit.value < totalCount.value) {
    offset.value += limit.value;
    loadNotifications();
  }
}

/* ───────── PDF EXPORT ───────── */
async function exportToPDF() {
  try {
    exporting.value = true;

    // Crea un nuovo documento PDF
    const doc = new jsPDF();

    // Titolo
    doc.setFontSize(20);
    doc.text("Report Notifiche", 14, 22);

    // Informazioni filtro
    doc.setFontSize(10);
    doc.setTextColor(100);
    let filterInfo = `Generato il: ${new Date().toLocaleDateString("it-IT")}`;
    if (activeFilterSummary.value) {
      filterInfo += ` | Filtro: ${activeFilterSummary.value}`;
    }
    doc.text(filterInfo, 14, 32);

    // Statistiche
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text(`Totale notifiche: ${notifications.value.length}`, 14, 42);
    doc.text(`Non lette: ${unreadCount.value}`, 14, 50);
    doc.text(`Errori: ${errorCount.value}`, 14, 58);

    // Tabella notifiche
    const tableData = notifications.value.map((notification, index) => [
      index + 1,
      formatDate(notification.createdAt),
      notification.title,
      getSeverityLabel(notification.severity),
      notification.is_read ? "Letti" : "Non letti",
      notification.graph_name || "N/A",
      notification.message.substring(0, 50) +
        (notification.message.length > 50 ? "..." : ""),
    ]);

    // @ts-ignore - jspdf-autotable types
    autoTable(doc, {
      startY: 65,
      head: [
        [
          "#",
          "Data/Ora",
          "Titolo",
          "Tipo di avviso",
          "Stato",
          "Dispositivo",
          "Messaggio",
        ],
      ],
      body: tableData,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 30 },
        2: { cellWidth: 40 },
        3: { cellWidth: 20 },
        4: { cellWidth: 15 },
        5: { cellWidth: 25 },
        6: { cellWidth: 50 },
      },
    });

    // Aggiungi dettagli per ogni notifica
    const finalY = (doc as any).lastAutoTable.finalY + 10;
    doc.setFontSize(14);
    doc.text("Dettaglio Notifiche", 14, finalY);

    let yPos = finalY + 10;

    notifications.value.forEach((notification, index) => {
      if (yPos > 280) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(`${index + 1}. ${notification.title}`, 14, yPos);

      yPos += 6;
      doc.setFont("helvetica", "normal");
      doc.text(`Data: ${formatDate(notification.createdAt)}`, 14, yPos);

      yPos += 6;
      doc.text(
        `Tipo di avviso: ${getSeverityLabel(notification.severity)} | Stato: ${
          notification.is_read ? "Letta" : "Non letta"
        }`,
        14,
        yPos
      );

      yPos += 6;
      doc.text(`Dispositivo: ${notification.graph_name || "N/A"}`, 14, yPos);

      yPos += 6;
      doc.setFont("helvetica", "bold");
      doc.text("Messaggio:", 14, yPos);

      yPos += 6;
      doc.setFont("helvetica", "normal");
      const messageLines = doc.splitTextToSize(notification.message, 180);
      doc.text(messageLines, 14, yPos);

      yPos += messageLines.length * 5 + 4;

      if (
        notification.admin_message &&
        notification.admin_message !== notification.message
      ) {
        doc.setFont("helvetica", "bold");
        doc.text("Messaggio Amministrativo:", 14, yPos);

        yPos += 6;
        doc.setFont("helvetica", "normal");
        const adminLines = doc.splitTextToSize(notification.admin_message, 180);
        doc.text(adminLines, 14, yPos);

        yPos += adminLines.length * 5 + 4;
      }

      yPos += 8;
    });

    // Salva il PDF
    const fileName = `notifiche_${new Date().toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);

    toast.success("PDF esportato con successo", {
      position: "top-right",
      duration: 3000,
    });
  } catch (error) {
    console.error("Errore nell'esportazione PDF:", error);
    toast.error("Errore nell'esportazione PDF", { position: "top-right" });
  } finally {
    exporting.value = false;
  }
}

/* ───────── WATCHERS ───────── */
watch([scope, includeRead, selectedSeverity, timeRange, startDate, endDate], () => {
  // console.log("selected severity in watcher:", selectedSeverity.value);
  // non c'è bisogno di aggiornare qui i queryParams perché è una computed
  // const params: any = {
  //   scope: scope.value,
  //   include_read: includeRead.value ? "1" : "0",
  //   limit: limit.value,
  //   offset: offset.value,
  //   severity:
  //     selectedSeverity.value !== "all" ? selectedSeverity.value : undefined,
  // };
  offset.value = 0; // Reset paginazione quando cambiano i filtri
  loadNotifications();
});

/* ───────── LIFECYCLE ───────── */
onMounted(() => {
  // Imposta il range predefinito a questa settimana
  const dates = calculateDateRange("day");
  startDate.value = dates.start;
  endDate.value = dates.end;

  loadNotifications();
});
</script>

<style scoped>
/* ───────── CONTAINER ───────── */
.data-container {
  position: fixed;
  top: 0;
  left: var(--sb-offset);
  width: calc(100% - var(--sb-offset));
  bottom: 0;
  overflow: auto;
  transition: left 0.25s ease, width 0.25s ease;
  background: #0b0e12;
}

/* ───────── BREADCRUMB ───────── */
.breadcrumb {
  position: sticky;
  top: 0;
  z-index: 10;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 24px;
  background: #0d0d0d;
  color: #e6e6e6;
  font: 600 18px/56px Inter, system-ui, sans-serif;
  border-bottom: 2px solid #1d3a3a;
}

.breadcrumb-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.device-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
}

.meta-divider {
  color: #35404a;
  font-size: 1.2rem;
  line-height: 1;
}

.header-meta-group {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0 12px;
  height: 32px;
  border-radius: 6px;
  margin-left: 12px;
}

.meta-label {
  text-transform: uppercase;
  font-size: 0.65rem;
  font-weight: 600;
  color: #8b95a1;
  letter-spacing: 0.5px;
}

.meta-value {
  color: #e3e3e3;
  font-size: 0.85rem;
  font-family: "Inter", sans-serif;
  font-weight: 500;
}

.meta-value.unread-badge {
  color: #22c55e;
  font-weight: 700;
}

/* ───────── BUTTONS ───────── */
.btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #008f8f;
  background: #008f8f;
  color: #fff;
  font: 600 14px/1 Inter, system-ui, sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn:hover:not(:disabled) {
  background: #00a7a7;
  border-color: #00a7a7;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-export {
  background: #3b82f6;
  border-color: #3b82f6;
}

.btn-export:hover:not(:disabled) {
  background: #2563eb;
  border-color: #2563eb;
}

.btn-mark-all {
  background: transparent;
  border-color: #2a2f34;
  color: #9fb0c2;
}

.btn-mark-all:hover:not(:disabled) {
  background: rgba(0, 143, 143, 0.1);
  border-color: #008f8f;
  color: #008f8f;
}

.btn-small {
  padding: 4px 12px;
  font-size: 0.75rem;
  border-radius: 4px;
}

.btn-reset-date {
  background: transparent;
  border-color: #2a2f34;
  color: #9fb0c2;
  padding: 6px 10px;
}

.btn-reset-date:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: #ef4444;
  color: #ef4444;
}

.export-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  display: inline-block;
}

/* ───────── FILTERS ───────── */
.filters-section {
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.filters-row {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-label {
  font-size: 0.875rem;
  color: #9fb0c2;
  font-weight: 500;
  white-space: nowrap;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #2a2f34;
  transition: 0.4s;
  border-radius: 34px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .toggle-slider {
  background-color: #22c55e;
}

input:checked + .toggle-slider:before {
  transform: translateX(26px);
}

.date-input {
  padding: 6px 10px;
  border: 1px solid #2a2f34;
  background: #1a1f26;
  color: #e6e6e6;
  border-radius: 6px;
  font: 500 14px/1 Inter, system-ui, sans-serif;
  min-width: 130px;
}

.date-input:focus {
  outline: none;
  border-color: #22c55e;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.15);
}

.filter-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding: 12px 16px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 8px;
}

.summary-icon {
  font-size: 1.2rem;
}

.summary-text {
  flex: 1;
  color: #3b82f6;
  font-weight: 500;
}

.btn-clear-filter {
  background: transparent;
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #ef4444;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-clear-filter:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: #ef4444;
}

/* ───────── STATS SECTION ───────── */
.stats-section {
  padding: 20px 24px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat-card {
  background: #11161d;
  border: 1px solid #252a32;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  border-color: #2a2f34;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.stat-icon {
  font-size: 2rem;
  opacity: 0.8;
}

.stat-icon.unread-icon {
  color: #22c55e;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: #e6e6e6;
  line-height: 1;
}

.stat-value.unread-stat {
  color: #22c55e;
}

.stat-label {
  font-size: 0.875rem;
  color: #9fb0c2;
  margin-top: 4px;
}

/* ───────── NOTIFICATIONS LIST ───────── */
.notifications-container {
  padding: 20px 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.loading-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #9fb0c2;
  gap: 16px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #22c55e;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #9fb0c2;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.5;
}

.empty-state h3 {
  color: #e6e6e6;
  margin-bottom: 8px;
  font-size: 1.25rem;
}

.empty-state p {
  font-size: 0.875rem;
  opacity: 0.8;
  margin-bottom: 16px;
}

.notifications-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ───────── NOTIFICATION CARD ───────── */
.notification-card {
  background: #11161d;
  border: 1px solid #252a32;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.notification-card:hover {
  transform: translateY(-2px);
  border-color: #2a2f34;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.notification-card.unread {
  border-left: 4px solid #22c55e;
}

.notification-card.read {
  opacity: 0.8;
}

.notification-card.severity-error {
  border-left-color: #ef4444;
}

.notification-card.severity-warning {
  border-left-color: #eab308;
}

.notification-card.severity-info {
  border-left-color: #3b82f6;
}

.notification-card.severity-success {
  border-left-color: #22c55e;
}

.notification-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}

.notification-badge {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-top: 6px;
  flex-shrink: 0;
}

.notification-badge.success {
  background: #22c55e;
}

.notification-badge.info {
  background: #3b82f6;
}

.notification-badge.warning {
  background: #eab308;
}

.notification-badge.error {
  background: #ef4444;
}

.notification-title {
  flex: 1;
  min-width: 0;
}

.notification-title h4 {
  margin: 0 0 4px 0;
  color: #e6e6e6;
  font-size: 1.1rem;
  font-weight: 600;
}

.notification-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.meta-date {
  font-size: 0.75rem;
  color: #9fb0c2;
}

.meta-graph,
.meta-user {
  font-size: 0.75rem;
  color: #8b95a1;
  font-style: italic;
}

.notification-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.action-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #2a2f34;
  background: #1a1f26;
  color: #9fb0c2;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 1rem;
}

.action-btn:hover {
  background: #252a32;
  border-color: #353b43;
  color: #fff;
}

.action-btn.delete-btn:hover {
  color: #ef4444;
  border-color: #ef4444;
}

.notification-body {
  margin-bottom: 16px;
}

.notification-message {
  margin: 0 0 12px 0;
  color: #d6dde6;
  line-height: 1.5;
  font-size: 0.9375rem;
}

.notification-payload pre {
  margin: 0;
  padding: 12px;
  background: #0f141a;
  color: #e6e6e6;
  font-size: 0.875rem;
  line-height: 1.6;
  max-height: 300px;
  overflow: auto;
  border-top: 1px solid #252a32;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* Style for sections in the payload */
.notification-payload pre .section-title {
  color: #3b82f6;
  font-weight: 600;
  margin-top: 8px;
  display: block;
}

.notification-payload pre .separator {
  color: #666;
  text-align: center;
  margin: 8px 0;
  display: block;
}

.admin-message {
  background: rgba(59, 130, 246, 0.1);
  border-left: 3px solid #3b82f6;
  padding: 12px;
  border-radius: 6px;
  margin: 12px 0;
}

.admin-label {
  font-size: 0.75rem;
  color: #3b82f6;
  font-weight: 600;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.admin-message p {
  margin: 0;
  color: #d6dde6;
  font-size: 0.875rem;
}

.notification-payload {
  margin-top: 12px;
}

.notification-payload details {
  border: 1px solid #252a32;
  border-radius: 6px;
  overflow: hidden;
}

.notification-payload summary {
  padding: 8px 12px;
  background: #1a1f26;
  color: #9fb0c2;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
}

.notification-payload summary:hover {
  background: #252a32;
}

.notification-payload pre {
  margin: 0;
  padding: 12px;
  background: #0f141a;
  color: #8b95a1;
  font-size: 0.75rem;
  max-height: 200px;
  overflow: auto;
  border-top: 1px solid #252a32;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
}

.notification-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.notification-type {
  display: flex;
  align-items: center;
  gap: 8px;
}

.type-badge {
  display: inline-block;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 500;
  color: #9fb0c2;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.severity-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.severity-badge.success {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.severity-badge.info {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.severity-badge.warning {
  background: rgba(234, 179, 8, 0.1);
  color: #eab308;
  border: 1px solid rgba(234, 179, 8, 0.3);
}

.severity-badge.error {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.notification-actions-footer {
  display: flex;
  gap: 8px;
}

/* ───────── PAGINATION ───────── */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-top: 32px;
  padding: 20px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid #2a2f34;
  background: #1a1f26;
  color: #9fb0c2;
  border-radius: 6px;
  cursor: pointer;
  font: 600 14px/1 Inter, system-ui, sans-serif;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #252a32;
  border-color: #353b43;
  color: #fff;
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-info {
  color: #9fb0c2;
  font-size: 0.875rem;
  font-weight: 500;
}

/* ───────── RESPONSIVE ───────── */
@media (max-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .filters-row {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group {
    justify-content: space-between;
  }
}

@media (max-width: 768px) {
  .data-container {
    left: 0;
    width: 100%;
  }

  .breadcrumb {
    padding: 12px 16px;
    height: auto;
    min-height: 56px;
    flex-direction: column;
    gap: 12px;
  }

  .breadcrumb-actions {
    width: 100%;
    display: flex;
    gap: 8px;
  }

  .filters-section {
    padding: 16px;
  }

  .filter-group {
    flex-wrap: wrap;
  }

  .date-input {
    min-width: 120px;
  }

  .stats-section {
    padding: 16px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .notifications-container {
    padding: 16px;
  }

  .notification-card {
    padding: 16px;
  }

  .notification-header {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .notification-actions {
    align-self: flex-end;
  }

  .notification-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .notification-actions-footer {
    width: 100%;
    justify-content: flex-end;
  }

  .pagination {
    flex-direction: column;
    gap: 12px;
  }
}

@media (max-width: 480px) {
  .header-meta-group {
    flex-direction: column;
    height: auto;
    padding: 8px;
    gap: 4px;
    margin-left: 0;
  }

  .filter-summary {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .btn-clear-filter {
    align-self: flex-end;
  }

  .notification-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-value {
    font-size: 1.5rem;
  }
}
</style>
